// Shared state + actions for patient-initiated org sharing (ACDPA-186).
// One unfiltered fetch of the shareable-org list is the single source of truth, hosted once by
// OrgSharingContext and consumed by every surface: the connected orgs shown in "Who can see my
// data", the Organizations section, and the org list inside the invite popup.
// Search/filtering is done CLIENT-SIDE (see filterOrgs) so filtering one surface never drops a
// connected org from another. share/unshare hit the kernel and then reload the list.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getShareableOrganizations,
  shareWithOrganization,
  unshareWithOrganization,
  ShareableOrg,
  SharingPolicy,
} from "../../../services/orgSharing";

export interface UseOrgSharing {
  orgs: ShareableOrg[];
  policy: SharingPolicy | null;
  loading: boolean;
  busyOrgId: number | null;
  toast: string;
  setToast: (msg: string) => void;
  /** Refetch the full (unfiltered) list; resolves with the fresh org tree. */
  reload: () => Promise<ShareableOrg[]>;
  /** Share with an org. Returns true on success. */
  share: (org: ShareableOrg) => Promise<boolean>;
  /**
   * Share with several locations after ONE confirmation (F-468). The kernel takes one location per
   * call, so this calls it once per location in order, reloads once, and sets one toast that
   * names what was shared, what is awaiting approval, and what failed.
   */
  shareMany: (orgs: ShareableOrg[]) => Promise<ShareManyResult>;
  /** Revoke a share. Returns true on success. */
  unshare: (org: ShareableOrg) => Promise<boolean>;
  // Derived, computed once here so call sites don't re-derive them.
  blocked: boolean;
  singleOrg: boolean;
  connected: ShareableOrg[];
  /** Locations with a PENDING request awaiting the org's approval (SHARE_REQUIRES_APPROVAL). */
  pending: ShareableOrg[];
  /** org_id -> name, for rendering a location as "Parent · Location". */
  nameById: Map<number, string>;
}

export interface ShareManyResult {
  shared: ShareableOrg[];
  failed: { org: ShareableOrg; error: string }[];
}

/** "A", "A and B", "A, B, and C" — how a sentence names a list. */
export const joinNames = (names: string[]): string =>
  names.length <= 1
    ? names[0] ?? ""
    : names.length === 2
    ? `${names[0]} and ${names[1]}`
    : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;

/**
 * The one toast after sharing with several locations: granted now, awaiting approval, failed —
 * each clause present only when it has members. `pendingIds` are the locations the fresh list
 * reports as pending (the org requires approval).
 */
export const shareManyToast = (
  result: ShareManyResult,
  pendingIds: Set<number>,
): string => {
  const granted = result.shared.filter((o) => !pendingIds.has(o.org_id)).map((o) => o.name);
  const requested = result.shared.filter((o) => pendingIds.has(o.org_id)).map((o) => o.name);
  const parts: string[] = [];
  if (granted.length) parts.push(`Shared your profile with ${joinNames(granted)}.`);
  if (requested.length)
    parts.push(`Request sent to ${joinNames(requested)}. You'll get access once they approve.`);
  result.failed.forEach(({ org, error }) => parts.push(`Could not share with ${org.name}: ${error}`));
  return parts.join(" ");
};

/**
 * Effective share lifecycle for a location. `share_status` is the source of truth when present;
 * older kernels omit it, so fall back to is_shared (accepted) else 'none'. A pending share always
 * has is_shared=false, so this never reports 'pending' unless the kernel explicitly said so.
 */
export const shareStatusOf = (
  org: Pick<ShareableOrg, "share_status" | "is_shared">,
): "none" | "pending" | "accepted" =>
  org.share_status ?? (org.is_shared ? "accepted" : "none");

/** "Parent · Location" for a child org, else the org's own name. */
export const orgDisplayName = (
  org: ShareableOrg,
  nameById: Map<number, string>,
): string => {
  if (org.parent_id != null) {
    const parent = nameById.get(org.parent_id);
    if (parent) return `${parent} · ${org.name}`;
  }
  return org.name;
};

const normalizeName = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Client-side name filter that also matches a location by its parent's name. Normalizes away
 * spaces/punctuation on both sides so "valley health" matches "Valley Health" — and so it keeps
 * working when the shared SimpleInput strips spaces from what the user types. Operates on a FLAT
 * list (e.g. the flattened locations used by the autocomplete dropdown).
 */
export const filterOrgs = (
  orgs: ShareableOrg[],
  query: string,
  nameById: Map<number, string>,
): ShareableOrg[] => {
  const q = normalizeName(query);
  if (!q) return orgs;
  return orgs.filter((o) => normalizeName(orgDisplayName(o, nameById)).includes(q));
};

/** Every child-location row across all parents (parents themselves excluded). */
export const flattenLocations = (orgs: ShareableOrg[]): ShareableOrg[] =>
  orgs.flatMap((o) => o.locations ?? []);

/**
 * Nested filter for the accordion: keeps a parent when the parent's own name matches (with ALL
 * its locations) or when at least one of its locations matches (with only the matching ones).
 * Parents with no match are dropped. Same normalization as filterOrgs.
 */
export const filterOrgTree = (
  parents: ShareableOrg[],
  query: string,
  nameById: Map<number, string>,
): ShareableOrg[] => {
  const q = normalizeName(query);
  if (!q) return parents;
  const out: ShareableOrg[] = [];
  for (const p of parents) {
    const parentMatches = normalizeName(p.name).includes(q);
    const locations = (p.locations ?? []).filter(
      (l) => parentMatches || normalizeName(orgDisplayName(l, nameById)).includes(q),
    );
    if (parentMatches || locations.length) out.push({ ...p, locations });
  }
  return out;
};

export const useOrgSharing = (): UseOrgSharing => {
  const [orgs, setOrgs] = useState<ShareableOrg[]>([]);
  const [policy, setPolicy] = useState<SharingPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyOrgId, setBusyOrgId] = useState<number | null>(null);
  const [toast, setToast] = useState("");

  // Returns the freshly-fetched org tree so share()/unshare() can read the resulting share_status
  // (React state set here isn't readable synchronously by the same callback).
  const reload = useCallback(async (): Promise<ShareableOrg[]> => {
    setLoading(true);
    try {
      const res = await getShareableOrganizations();
      const list = res.organizations || [];
      setOrgs(list);
      setPolicy(res.sharing_policy || null);
      return list;
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setToast(err?.response?.data?.error || "Could not load organizations.");
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const share = useCallback(
    async (org: ShareableOrg): Promise<boolean> => {
      setBusyOrgId(org.org_id);
      try {
        await shareWithOrganization(org.org_id);
        // The org may require approval — the resulting share_status tells us whether access was
        // granted now or the request is pending (the app can't know the org's policy up front).
        const list = await reload();
        const fresh = flattenLocations(list).find((o) => o.org_id === org.org_id);
        setToast(
          fresh && shareStatusOf(fresh) === "pending"
            ? `Request sent to ${org.name}. You'll get access once they approve.`
            : `Shared your profile with ${org.name}.`,
        );
        return true;
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } };
        setToast(err?.response?.data?.error || "Could not share. Please try again.");
        return false;
      } finally {
        setBusyOrgId(null);
      }
    },
    [reload],
  );

  const shareMany = useCallback(
    async (targets: ShareableOrg[]): Promise<ShareManyResult> => {
      const result: ShareManyResult = { shared: [], failed: [] };
      // One call per location, in order: the kernel's share endpoint takes one location, and a
      // later refusal (e.g. the policy cap) must not undo an earlier success.
      for (const org of targets) {
        setBusyOrgId(org.org_id);
        try {
          await shareWithOrganization(org.org_id);
          result.shared.push(org);
        } catch (e) {
          const err = e as { response?: { data?: { error?: string } } };
          result.failed.push({
            org,
            error: err?.response?.data?.error || "Please try again.",
          });
        }
      }
      setBusyOrgId(null);
      const list = await reload();
      const pendingIds = new Set(
        flattenLocations(list)
          .filter((o) => shareStatusOf(o) === "pending")
          .map((o) => o.org_id),
      );
      setToast(shareManyToast(result, pendingIds));
      return result;
    },
    [reload],
  );

  const unshare = useCallback(
    async (org: ShareableOrg): Promise<boolean> => {
      // The same DELETE revokes an accepted share OR withdraws a pending request (kernel handles
      // both); the toast reflects which one it was, read from the row we're acting on.
      const wasPending = shareStatusOf(org) === "pending";
      setBusyOrgId(org.org_id);
      try {
        await unshareWithOrganization(org.org_id);
        setToast(
          wasPending
            ? `Request to ${org.name} cancelled.`
            : `Stopped sharing with ${org.name}.`,
        );
        await reload();
        return true;
      } catch (e) {
        const err = e as { response?: { data?: { error?: string } } };
        setToast(err?.response?.data?.error || "Could not update sharing.");
        return false;
      } finally {
        setBusyOrgId(null);
      }
    },
    [reload],
  );

  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    // Names for both the parent headers and their nested locations, so a location renders as
    // "Parent · Location" wherever orgDisplayName is used (connected list, filters).
    orgs.forEach((p) => {
      m.set(p.org_id, p.name);
      (p.locations ?? []).forEach((l) => m.set(l.org_id, l.name));
    });
    return m;
  }, [orgs]);

  const blocked = policy?.policy === "blocked";
  const singleOrg = policy?.policy === "single_org";
  // Connected = the LOCATIONS the patient actively shares with (parents are never share targets).
  const connected = useMemo(() => flattenLocations(orgs).filter((o) => o.is_shared), [orgs]);
  // Pending = locations with a request awaiting the org's approval (SHARE_REQUIRES_APPROVAL). Kept
  // separate from `connected` because the org cannot see the patient yet — the UI badges these
  // "Awaiting approval" with a Cancel-request action.
  const pending = useMemo(
    () => flattenLocations(orgs).filter((o) => shareStatusOf(o) === "pending"),
    [orgs],
  );

  return {
    orgs,
    policy,
    loading,
    busyOrgId,
    toast,
    setToast,
    reload,
    share,
    shareMany,
    unshare,
    blocked,
    singleOrg,
    connected,
    pending,
    nameById,
  };
};
